/**
 * Shared subscription picker.
 * @package @app/api
 *
 * Both `subscription.current` (drives the profile UI) and `createContext`
 * (drives rate-limit tier + plan context) need to resolve "which subscription
 * row best represents this user's current state." If they used different
 * `orderBy` rules — e.g. one by createdAt, another by periodEnd — the same
 * user could see the profile claim Pro while API rate-limits silently
 * dropped them to free (or vice versa).
 *
 * This module is the single source of truth for that pick.
 */

import type { getEnhancedPrisma } from '@app/db';

type EnhancedPrismaClient = ReturnType<typeof getEnhancedPrisma>;

/**
 * Status priority for picking the "most current" subscription when a user
 * has multiple rows (e.g. a stale `incomplete` checkout attempt sitting
 * alongside an `active` sub from a prior upgrade).
 *
 * Lower number wins. The order reflects business semantics:
 *   active     — fully paid, gets all features
 *   trialing   — in trial, gets all features
 *   past_due   — dunning grace window, still gets access per Stripe
 *   incomplete — checkout completed but webhook hasn't backfilled yet;
 *                may evaporate if Stripe never confirms
 */
const STATUS_PRIORITY: Record<string, number> = {
  active: 0,
  trialing: 1,
  past_due: 2,
  incomplete: 3,
};

const STATUS_FILTER = ['active', 'trialing', 'past_due', 'incomplete'] as const;

/**
 * Find the canonical "current" subscription for a user. Returns null when
 * the user has no rows in any of the matched statuses.
 *
 * Pick rule (in order):
 *   1. Lowest STATUS_PRIORITY (active > trialing > past_due > incomplete)
 *   2. Latest periodEnd (nulls last)
 *   3. Latest createdAt
 */
export async function findActiveSubscription(
  db: EnhancedPrismaClient,
  userId: string,
): Promise<{
  id: string;
  plan: string | null;
  status: string | null;
  periodStart: Date | null;
  periodEnd: Date | null;
  trialStart: Date | null;
  trialEnd: Date | null;
  cancelAtPeriodEnd: boolean;
  billingInterval: string | null;
} | null> {
  // Match rows the Better-Auth Stripe plugin creates (referenceType IS NULL)
  // as well as rows we write with referenceType='user'. The plugin uses
  // referenceId alone; referenceType is our extension for org-scoped rows.
  const rows = await db.subscription.findMany({
    where: {
      referenceId: userId,
      OR: [{ referenceType: 'user' }, { referenceType: null }],
      status: { in: [...STATUS_FILTER] },
    },
    select: {
      id: true,
      plan: true,
      status: true,
      periodStart: true,
      periodEnd: true,
      trialStart: true,
      trialEnd: true,
      cancelAtPeriodEnd: true,
      billingInterval: true,
      createdAt: true,
    },
  });

  if (rows.length === 0) return null;
  if (rows.length === 1) {
    const { createdAt: _createdAt, ...row } = rows[0]!;
    return row;
  }

  const sorted = [...rows].sort((a, b) => {
    const ap = STATUS_PRIORITY[a.status ?? ''] ?? 99;
    const bp = STATUS_PRIORITY[b.status ?? ''] ?? 99;
    if (ap !== bp) return ap - bp;

    // periodEnd desc, nulls last
    const aEnd = a.periodEnd?.getTime();
    const bEnd = b.periodEnd?.getTime();
    if (aEnd && bEnd && aEnd !== bEnd) return bEnd - aEnd;
    if (aEnd && !bEnd) return -1;
    if (!aEnd && bEnd) return 1;

    // createdAt desc
    return b.createdAt.getTime() - a.createdAt.getTime();
  });

  const { createdAt: _createdAt, ...row } = sorted[0]!;
  return row;
}
